// A-7b PR 6a — Garment × body styling pattern matrix.
//
// 51 hardcoded patterns covering women's tops, bottoms, dresses, outerwear,
// knitwear, jumpsuits. Men's wear deferred to A-7c.
//
// Patterns are inert until PR 6b wires `T2_GARMENT_ATTRIBUTES` into
// `computeFitScore`. PR 6a ships matrix + matcher + interpolator only.
//
// Per memory: LB generators DO NOT consume `styling_pattern_id` /
// `styling_copy`. UI shape unchanged. Forward-flow only.

import type { GarmentAttributes } from "./unifiedScoring";

// ---- Flag ---------------------------------------------------------------

// Default TRUE (vs A-6's false-default) — A-7b is purely additive: only
// ever adds a row, never removes one. Rollback = flip to false.
// PR 6a: flag exists, but T2_GARMENT_ATTRIBUTES is not wired into
// computeFitScore yet (PR 6b). Until then, this flag has no effect.
export const STYLING_INTELLIGENCE = true;

// ---- Types --------------------------------------------------------------

export type AttrMatch = string | string[];

export interface StylingPatternAttrMatch {
  silhouette?: AttrMatch;
  length?: AttrMatch;
  neckline?: AttrMatch;
  sleeve_length?: AttrMatch;
  fit?: AttrMatch;
  fabric_weight?: AttrMatch;
  structure?: AttrMatch;
  fabric_stretch_enum?: AttrMatch;
}

export interface StylingPattern {
  id: string;
  category_filter: string[];
  attribute_match: StylingPatternAttrMatch;
  body_shape_match: string[];
  body_size_match: string[] | "any";
  height_match?: string[];
  copy_template: string;
  priority: number;
}

// ---- Pattern matrix (51 patterns, locked) -------------------------------

export const STYLING_PATTERNS: StylingPattern[] = [
  // ===== TOPS (13 patterns) =====
  {
    id: "T1_fitted_slim_hourglass",
    category_filter: ["top"],
    attribute_match: { silhouette: "fitted", fit: "slim" },
    body_shape_match: ["hourglass"],
    body_size_match: "any",
    copy_template:
      "Fitted cuts on this {item} work well with hourglass shapes — your usual size should sit cleanly.",
    priority: 90,
  },
  {
    id: "T2_oversized_hourglass",
    category_filter: ["top"],
    attribute_match: { silhouette: "oversized", fit: "oversized" },
    body_shape_match: ["hourglass"],
    body_size_match: "any",
    copy_template:
      "Oversized cuts on this {item} can mask the waist — belting or layering keeps the silhouette balanced.",
    priority: 85,
  },
  {
    id: "T3_boxy_athletic",
    category_filter: ["top"],
    attribute_match: { silhouette: "boxy", fit: "relaxed" },
    body_shape_match: ["athletic", "athletic_broad"],
    body_size_match: "any",
    copy_template:
      "Boxy cuts on this {item} work well with athletic frames — relaxed across the shoulders.",
    priority: 85,
  },
  {
    id: "T4_volume_sleeves_athletic_broad",
    category_filter: ["top"],
    attribute_match: { sleeve_length: ["puff", "bell", "flutter"] },
    body_shape_match: ["athletic_broad"],
    body_size_match: "any",
    copy_template:
      "Volume sleeves on this {item} add softness — a nice contrast to athletic frames.",
    priority: 80,
  },
  {
    id: "T5_peplum_pear_curvy",
    category_filter: ["top"],
    attribute_match: { silhouette: "peplum" },
    body_shape_match: ["pear", "curvy"],
    body_size_match: "any",
    copy_template:
      "Peplum cuts on this {item} flatter pear shapes — defined waist, soft over hips.",
    priority: 90,
  },
  {
    id: "T6_draped_softshape",
    category_filter: ["top"],
    attribute_match: { silhouette: "draped", structure: "drapey" },
    body_shape_match: ["straight", "curvy"],
    body_size_match: "any",
    copy_template:
      "Draped {item} adds movement — works well on softer silhouettes.",
    priority: 80,
  },
  {
    id: "T7_stretch_broader_builds",
    category_filter: ["top"],
    attribute_match: { fit: "fitted", fabric_stretch_enum: ["moderate", "high"] },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: ["broad", "plus"],
    copy_template:
      "Stretch fabric in this {item} gives comfortable movement — your usual size should work.",
    priority: 75,
  },
  {
    id: "T8_vneck_plunge_broader",
    category_filter: ["top"],
    attribute_match: { neckline: ["v_neck", "plunge"] },
    body_shape_match: ["athletic_broad", "curvy"],
    body_size_match: "any",
    copy_template:
      "V-neckline on this {item} elongates the upper body — flattering on broader builds.",
    priority: 80,
  },
  {
    id: "T9_cowl_curves",
    category_filter: ["top"],
    attribute_match: { neckline: "cowl" },
    body_shape_match: ["hourglass", "curvy"],
    body_size_match: "any",
    copy_template:
      "Cowl neckline on this {item} drapes softly — flattering on curves.",
    priority: 80,
  },
  {
    id: "T10_halter_athletic",
    category_filter: ["top"],
    attribute_match: { neckline: "halter" },
    body_shape_match: ["athletic", "athletic_broad"],
    body_size_match: "any",
    copy_template:
      "Halter neckline on this {item} highlights the shoulders — works well with athletic frames.",
    priority: 80,
  },
  {
    id: "T11_wide_neckline_balance",
    category_filter: ["top"],
    attribute_match: { neckline: ["boat", "square"] },
    body_shape_match: ["hourglass", "pear"],
    body_size_match: "any",
    copy_template:
      "Wide neckline on this {item} balances proportions — flattering on your shape.",
    priority: 75,
  },
  {
    id: "T12_cropped_tall",
    category_filter: ["top"],
    attribute_match: { length: "cropped", silhouette: ["fitted", "boxy"] },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: "any",
    height_match: ["tall"],
    copy_template:
      "Cropped {item} emphasizes tall frames — pairs well with high-waisted bottoms.",
    priority: 78,
  },
  {
    id: "T13_light_relaxed_broader",
    category_filter: ["top"],
    attribute_match: { fit: "relaxed", fabric_weight: "light" },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: ["broad", "plus"],
    copy_template:
      "Light relaxed cut on this {item} gives breathable comfort — your usual size should work.",
    priority: 72,
  },

  // ===== BOTTOMS (8 patterns) =====
  {
    id: "B1_wide_leg_hip_balance",
    category_filter: ["bottom"],
    attribute_match: { silhouette: ["wide_leg", "flared"] },
    body_shape_match: ["hourglass", "pear", "curvy"],
    body_size_match: "any",
    copy_template:
      "Wide-leg cuts on these {item} balance hip width — flattering with your build.",
    priority: 90,
  },
  {
    id: "B2_straight_clean_line",
    category_filter: ["bottom"],
    attribute_match: { silhouette: "straight", fit: "slim" },
    body_shape_match: ["athletic", "straight"],
    body_size_match: "any",
    copy_template:
      "Straight cuts on these {item} give a clean line — works with your build.",
    priority: 85,
  },
  {
    id: "B3_tapered_hip_balance",
    category_filter: ["bottom"],
    attribute_match: { silhouette: "tapered", fit: "slim" },
    body_shape_match: ["pear", "curvy"],
    body_size_match: "any",
    copy_template:
      "Tapered cuts on these {item} narrow at the ankle — balance wider hips nicely.",
    priority: 85,
  },
  {
    id: "B4_stretch_close_fit",
    category_filter: ["bottom"],
    attribute_match: { fit: "slim", fabric_stretch_enum: "high" },
    body_shape_match: ["curvy", "plus_size"],
    body_size_match: "any",
    copy_template:
      "Stretch in these slim {item} gives a comfortable close fit.",
    priority: 78,
  },
  {
    id: "B5_cropped_tall_emphasis",
    category_filter: ["bottom"],
    attribute_match: { length: ["cropped", "ankle"] },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: "any",
    height_match: ["tall"],
    copy_template:
      "Cropped length on these {item} emphasizes tall frames — works well on you.",
    priority: 78,
  },
  {
    id: "B6_full_length_petite_caution",
    category_filter: ["bottom"],
    attribute_match: { length: "full_length", fit: "fitted" },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: "any",
    height_match: ["petite"],
    copy_template:
      "Full-length fitted {item} can shorten petite frames — cropped or ankle lengths give better proportion.",
    priority: 80,
  },
  {
    id: "B7_high_waist_waist_define",
    category_filter: ["bottom"],
    attribute_match: { silhouette: ["paperbag", "high_waist"] },
    body_shape_match: ["pear", "hourglass"],
    body_size_match: "any",
    copy_template:
      "High-waist cut on these {item} defines the waist — flattering on your shape.",
    priority: 85,
  },
  {
    id: "B8_skinny_athletic",
    category_filter: ["bottom"],
    attribute_match: { fit: "slim" },
    body_shape_match: ["athletic"],
    body_size_match: "any",
    copy_template:
      "Slim cuts on these {item} follow athletic lines — your usual size should sit cleanly.",
    priority: 75,
  },

  // ===== DRESSES (12 patterns) =====
  {
    id: "D1_fit_and_flare_waist",
    category_filter: ["dress"],
    attribute_match: { silhouette: "fit_and_flare" },
    body_shape_match: ["hourglass", "pear", "curvy"],
    body_size_match: "any",
    copy_template:
      "Fit-and-flare cut on this {item} emphasizes the waist — flattering on your shape.",
    priority: 95,
  },
  {
    id: "D2_bodycon_curves",
    category_filter: ["dress"],
    attribute_match: { silhouette: "bodycon", fit: "fitted" },
    body_shape_match: ["hourglass", "curvy"],
    body_size_match: ["average", "broad"],
    copy_template:
      "Bodycon cut on this {item} works well with curves — your usual size should sit cleanly.",
    priority: 90,
  },
  {
    id: "D3_a_line_pear",
    category_filter: ["dress"],
    attribute_match: { silhouette: "a_line" },
    body_shape_match: ["pear", "curvy"],
    body_size_match: "any",
    copy_template:
      "A-line cut on this {item} flatters pear shapes — soft over hips, fitted at top.",
    priority: 90,
  },
  {
    id: "D4_sheath_straight",
    category_filter: ["dress"],
    attribute_match: { silhouette: "sheath", fit: "fitted" },
    body_shape_match: ["straight", "athletic"],
    body_size_match: "any",
    copy_template:
      "Sheath cut on this {item} gives a clean column — works well on straight frames.",
    priority: 88,
  },
  {
    id: "D5_wrap_universal",
    category_filter: ["dress"],
    attribute_match: { silhouette: "wrap" },
    body_shape_match: ["hourglass", "pear", "curvy"],
    body_size_match: "any",
    copy_template:
      "Wrap cut on this {item} defines the waist naturally — flattering across body shapes.",
    priority: 92,
  },
  {
    id: "D6_relaxed_athletic",
    category_filter: ["dress"],
    attribute_match: { silhouette: ["oversized", "boxy"] },
    body_shape_match: ["athletic", "athletic_broad"],
    body_size_match: "any",
    copy_template:
      "Relaxed cut on this {item} works well with athletic frames — comfortable across the shoulders.",
    priority: 80,
  },
  {
    id: "D7_midi_high_neck_tall",
    category_filter: ["dress"],
    attribute_match: {
      length: "midi",
      neckline: ["high_neck", "mock_neck", "turtleneck"],
    },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: "any",
    height_match: ["tall"],
    copy_template:
      "High-neck midi {item} emphasizes tall proportions — clean and elegant.",
    priority: 78,
  },
  {
    id: "D8_mini_pear_layer",
    category_filter: ["dress"],
    attribute_match: { length: "mini" },
    body_shape_match: ["pear"],
    body_size_match: "any",
    copy_template:
      "Mini length on this {item} works best paired with longer layers — gives balanced proportion on your shape.",
    priority: 75,
  },
  {
    id: "D9_mini_fitted_hourglass",
    category_filter: ["dress"],
    attribute_match: { length: "mini", fit: "fitted" },
    body_shape_match: ["hourglass"],
    body_size_match: ["average", "broad"],
    copy_template:
      "Fitted mini {item} works well with hourglass shapes — your usual size should sit cleanly.",
    priority: 85,
  },
  {
    id: "D10_maxi_tall",
    category_filter: ["dress"],
    attribute_match: { length: "maxi", silhouette: ["a_line", "draped"] },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: "any",
    height_match: ["tall"],
    copy_template:
      "Maxi length on this {item} suits tall frames — the silhouette flows well.",
    priority: 78,
  },
  {
    id: "D11_maxi_petite_caution",
    category_filter: ["dress"],
    attribute_match: { length: "maxi" },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: "any",
    height_match: ["petite"],
    copy_template:
      "Maxi length on this {item} can overwhelm petite frames — hemmed or midi alternatives may proportion better.",
    priority: 78,
  },
  {
    id: "D12_tiered_pear_curvy",
    category_filter: ["dress"],
    attribute_match: { silhouette: "tiered" },
    body_shape_match: ["pear", "curvy"],
    body_size_match: "any",
    copy_template:
      "Tiered cut on this {item} adds soft volume below the waist — flattering on pear shapes.",
    priority: 82,
  },
  // PR 6c-1: fitted+midi dress coverage (previously no D-series fired on this
  // very common combo — surfaced by Mango ruched-dress device test).
  {
    id: "D13_fitted_midi_hourglass",
    category_filter: ["dress"],
    attribute_match: { silhouette: "fitted", length: "midi" },
    body_shape_match: ["hourglass"],
    body_size_match: "any",
    copy_template:
      "Fitted midi {item} works well with hourglass shapes — your usual size should sit cleanly.",
    priority: 80,
  },
  {
    id: "D14_fitted_midi_curvy",
    category_filter: ["dress"],
    attribute_match: { silhouette: "fitted", length: "midi" },
    body_shape_match: ["curvy"],
    body_size_match: ["slim", "average"],
    copy_template:
      "Fitted midi {item} flatters curves — your usual size should give a clean line.",
    priority: 78,
  },
  {
    id: "D15_fitted_midi_pear",
    category_filter: ["dress"],
    attribute_match: { silhouette: "fitted", length: "midi" },
    body_shape_match: ["pear"],
    body_size_match: "any",
    copy_template:
      "Fitted midi {item} defines the waist while skimming over hips — flattering on pear shapes.",
    priority: 75,
  },

  // ===== OUTERWEAR (8 patterns) =====
  {
    id: "O1_fitted_cropped_hourglass",
    category_filter: ["outerwear"],
    attribute_match: { silhouette: "fitted", length: "cropped" },
    body_shape_match: ["hourglass", "curvy"],
    body_size_match: "any",
    copy_template:
      "Fitted cropped {item} emphasizes the waist — flattering on hourglass and curvy shapes.",
    priority: 88,
  },
  {
    id: "O2_longline_oversized_athletic",
    category_filter: ["outerwear"],
    attribute_match: { silhouette: "oversized", length: "longline" },
    body_shape_match: ["athletic", "athletic_broad"],
    body_size_match: "any",
    copy_template:
      "Longline oversized cut on this {item} drapes well with athletic frames.",
    priority: 82,
  },
  {
    id: "O3_structured_athletic",
    category_filter: ["outerwear"],
    attribute_match: { silhouette: "structured", fit: "fitted" },
    body_shape_match: ["athletic", "athletic_broad"],
    body_size_match: "any",
    copy_template:
      "Structured tailoring on this {item} works well with athletic builds.",
    priority: 85,
  },
  {
    id: "O4_oversized_petite_caution",
    category_filter: ["outerwear"],
    attribute_match: { silhouette: "oversized" },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: "any",
    height_match: ["petite"],
    copy_template:
      "Oversized {item} can overwhelm petite frames — sizing down may give a better proportion.",
    priority: 80,
  },
  {
    id: "O5_fitted_curves",
    category_filter: ["outerwear"],
    attribute_match: { silhouette: "fitted" },
    body_shape_match: ["pear", "curvy"],
    body_size_match: "any",
    copy_template:
      "Fitted cut on this {item} defines the upper body — flattering with curves below.",
    priority: 80,
  },
  {
    id: "O6_draped_soft_movement",
    category_filter: ["outerwear"],
    attribute_match: { silhouette: "draped", structure: "drapey" },
    body_shape_match: ["straight", "curvy"],
    body_size_match: "any",
    copy_template:
      "Draped {item} adds soft movement — works well across body shapes.",
    priority: 75,
  },
  {
    id: "O7_long_fitted_tall",
    category_filter: ["outerwear"],
    attribute_match: { length: ["midi", "full_length"], fit: "fitted" },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: "any",
    height_match: ["tall"],
    copy_template:
      "Long fitted {item} emphasizes tall proportions — flattering on your height.",
    priority: 78,
  },
  {
    id: "O8_boxy_cropped_hourglass",
    category_filter: ["outerwear"],
    attribute_match: { silhouette: "boxy", length: "cropped" },
    body_shape_match: ["hourglass"],
    body_size_match: "any",
    copy_template:
      "Cropped boxy {item} pairs well with high-waisted bottoms — emphasizes the waist.",
    priority: 82,
  },

  // ===== KNITWEAR (5 patterns) =====
  {
    id: "K1_chunky_knit_volume",
    category_filter: ["top", "dress"],
    attribute_match: {
      silhouette: "oversized",
      fabric_weight: "heavy",
      structure: "soft",
    },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: ["slim", "average"],
    copy_template:
      "Chunky knit on this {item} adds soft volume — sizing down may give a closer fit.",
    priority: 70,
  },
  {
    id: "K2_ribbed_hug",
    category_filter: ["top", "dress"],
    attribute_match: { fit: "fitted", fabric_stretch_enum: ["moderate", "high"] },
    body_shape_match: ["hourglass", "curvy"],
    body_size_match: "any",
    copy_template:
      "Ribbed knit on this {item} hugs the shape — emphasizes the silhouette.",
    priority: 73,
  },
  {
    id: "K3_longline_drape_tall",
    category_filter: ["top"],
    attribute_match: { length: "longline", silhouette: "draped" },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: "any",
    height_match: ["tall"],
    copy_template: "Longline {item} drapes well with tall frames.",
    priority: 70,
  },
  {
    id: "K4_cropped_boxy_knit",
    category_filter: ["top"],
    attribute_match: { length: "cropped", silhouette: "boxy" },
    body_shape_match: ["hourglass", "curvy"],
    body_size_match: "any",
    copy_template:
      "Cropped boxy {item} pairs well with high-waisted bottoms.",
    priority: 73,
  },
  {
    id: "K5_high_neck_athletic",
    category_filter: ["top", "dress"],
    attribute_match: {
      fit: "fitted",
      neckline: ["turtleneck", "mock_neck"],
    },
    body_shape_match: ["athletic", "athletic_broad"],
    body_size_match: "any",
    copy_template:
      "High-neck {item} elongates the upper body — clean lines on athletic frames.",
    priority: 75,
  },

  // ===== JUMPSUITS / DEFENSIVE EDGE CASES (5 patterns, currently inert) =====
  // Categories 'jumpsuit' / 'playsuit' are not yet emitted by detected_items —
  // patterns stay inert until vocabulary catches up. No risk; deferred to A-7c.
  {
    id: "J1_wide_leg_jumpsuit",
    category_filter: ["jumpsuit"],
    attribute_match: { silhouette: "wide_leg" },
    body_shape_match: ["hourglass", "pear", "curvy"],
    body_size_match: "any",
    copy_template:
      "Wide-leg {item} balances hip width — flattering with your build.",
    priority: 88,
  },
  {
    id: "J2_fitted_jumpsuit_curves",
    category_filter: ["jumpsuit"],
    attribute_match: { fit: "fitted" },
    body_shape_match: ["hourglass", "curvy"],
    body_size_match: ["average", "broad"],
    copy_template:
      "Fitted {item} works well with curves — your usual size should sit cleanly.",
    priority: 85,
  },
  {
    id: "J3_cropped_jumpsuit_tall",
    category_filter: ["jumpsuit"],
    attribute_match: { length: "cropped" },
    body_shape_match: [
      "hourglass", "pear", "curvy", "athletic", "athletic_broad",
      "straight", "plus_size",
    ],
    body_size_match: "any",
    height_match: ["tall"],
    copy_template:
      "Cropped {item} emphasizes tall frames — works well on you.",
    priority: 80,
  },
  {
    id: "J4_fitted_mini_playsuit",
    category_filter: ["jumpsuit", "playsuit", "dress"],
    attribute_match: { length: "mini", fit: "fitted" },
    body_shape_match: ["hourglass", "athletic"],
    body_size_match: ["average"],
    copy_template:
      "Fitted mini {item} flatters athletic and hourglass frames.",
    priority: 78,
  },
  {
    id: "J5_open_neckline_jumpsuit_broader",
    category_filter: ["jumpsuit"],
    attribute_match: { neckline: ["v_neck", "plunge", "halter"] },
    body_shape_match: ["athletic_broad", "curvy"],
    body_size_match: "any",
    copy_template:
      "Open neckline on this {item} elongates the upper body — flattering on broader builds.",
    priority: 78,
  },
];

// ---- Normalizers (used by matcher + ResolvedSignals) -------------------

export function normalizeBodyShape(raw: string | null): string | null {
  if (!raw) return null;
  const key = raw.toLowerCase().trim();
  const map: Record<string, string> = {
    hourglass: "hourglass",
    pear: "pear",
    athletic: "athletic",
    athletic_broad: "athletic_broad",
    curvy: "curvy",
    straight: "straight",
    plus_size: "plus_size",
    broad: "athletic_broad",
    stocky: "athletic_broad",
    regular: "straight",
    regular_average: "straight",
  };
  return map[key] ?? null;
}

export function normalizeBodySize(raw: string | null): string | null {
  if (!raw) return null;
  const key = raw.toLowerCase().trim();
  const map: Record<string, string> = {
    slim_lean: "slim",
    slim: "slim",
    average: "average",
    regular_average: "average",
    fuller: "broad",
    broad: "broad",
    plus_size: "plus",
    plus: "plus",
    stocky: "broad",
    athletic_broad: "broad",
  };
  return map[key] ?? null;
}

export type HeightBucket = "petite" | "regular" | "tall";

export function normalizeHeightBucket(raw: string | null): HeightBucket | null {
  if (!raw) return null;
  const key = raw.toLowerCase().trim();
  if (!key) return null;
  const petite = ["under_5_4", "5_0_to_5_3", "petite"];
  const tall = [
    "over_5_7", "tall", "average_tall", "5_8_to_5_11", "over_5_11",
  ];
  if (petite.some((p) => key.includes(p))) return "petite";
  if (tall.some((t) => key.includes(t))) return "tall";
  if (key === "average") return "regular";
  return "regular";
}

// ---- Matcher ------------------------------------------------------------

function attrSatisfies(
  match: AttrMatch | undefined,
  value: string | null | undefined,
): boolean {
  if (match === undefined) return true; // pattern doesn't constrain this attr
  if (value == null) return false;
  if (Array.isArray(match)) return match.includes(value);
  return match === value;
}

function patternMatches(
  p: StylingPattern,
  attrs: GarmentAttributes,
  body_shape: string,
  body_size: string | null,
  height: HeightBucket | null,
  category: string,
): boolean {
  if (!p.category_filter.includes(category)) return false;

  const m = p.attribute_match;
  if (!attrSatisfies(m.silhouette, attrs.silhouette)) return false;
  if (!attrSatisfies(m.length, attrs.length)) return false;
  if (!attrSatisfies(m.neckline, attrs.neckline)) return false;
  if (!attrSatisfies(m.sleeve_length, attrs.sleeve_length)) return false;
  if (!attrSatisfies(m.fit, attrs.fit)) return false;
  if (!attrSatisfies(m.fabric_weight, attrs.fabric_weight)) return false;
  if (!attrSatisfies(m.structure, attrs.structure)) return false;
  if (!attrSatisfies(m.fabric_stretch_enum, attrs.fabric_stretch_enum)) return false;

  if (!p.body_shape_match.includes(body_shape)) return false;

  if (p.body_size_match !== "any") {
    if (!body_size || !p.body_size_match.includes(body_size)) return false;
  }

  if (p.height_match) {
    if (!height || !p.height_match.includes(height)) return false;
  }

  return true;
}

/**
 * Find the highest-priority pattern that matches the given garment attributes
 * + body profile + category. Tie-breaker: alphabetical id.
 * Returns null when no pattern matches. ONE pattern per card — avoids row spam.
 */
export function findBestStylingPattern(
  attrs: GarmentAttributes | null,
  body_shape: string | null,
  body_size: string | null,
  height: HeightBucket | null,
  category: string | null,
): StylingPattern | null {
  if (!attrs || !body_shape || !category) return null;
  const cat = category.toLowerCase().trim();
  const matches = STYLING_PATTERNS.filter((p) =>
    patternMatches(p, attrs, body_shape, body_size, height, cat),
  );
  if (matches.length === 0) return null;
  matches.sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return a.id.localeCompare(b.id);
  });
  return matches[0];
}

// PR 6c.1 — strip descriptor tokens from item_name that already appear in
// the template prefix (text before {item}), so combinations like
//   template: "Fitted mini {item} flatters..."
//   item:     "ruffled wrap mini dress"
// render as "Fitted mini ruffled wrap dress flatters..." rather than the
// awkward "Fitted mini ruffled wrap mini dress flatters...".
//
// Algorithm:
// 1. Tokenize the template prefix (everything before {item}), lowercase.
// 2. Tokenize item_name, lowercase.
// 3. Drop any item token that matches a prefix token (set membership).
// 4. If <2 tokens remain, fall back to categoryFallback (or original).
export function interpolateItem(
  template: string,
  itemName: string,
  categoryFallback?: string | null,
): string {
  const idx = template.indexOf("{item}");
  if (idx < 0) return template;
  const prefix = template.slice(0, idx);
  const prefixTokens = new Set(
    prefix
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter((t) => t.length > 0),
  );
  const itemTokens = itemName.split(/\s+/).filter(Boolean);
  const kept = itemTokens.filter(
    (t) => !prefixTokens.has(t.toLowerCase().replace(/[^a-z]/g, "")),
  );
  let finalItem = kept.join(" ").trim();
  if (kept.length < 2) {
    const fallback = (categoryFallback ?? "").trim();
    finalItem = fallback.length > 0 ? fallback : itemName;
  }
  return template.replace(/\{item\}/g, finalItem);
}

